import {Icon} from '@iconify/react';
/* eslint-disable @typescript-eslint/no-explicit-any */
import interact from 'interactjs';
import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useDebouncedCallback} from 'use-debounce';
import {getElementShadow} from '../../incase-domain/common/elementShadow';
import {IElement} from '../../incase-domain/elements';
import {ELEMENT} from '../../incase-domain/elements/base/ElementTypes';
import {isCoverHtmlElement} from '../../incase-domain/elements/html/html';
import {IElementQuestion} from '../../incase-domain/elements/question/question';
import {showGridSelector} from '../../store/selectors/grid';
import {highlightedElementIndexSelector} from '../../store/selectors/slide.selectors';
import BaseElementWrapper from '../../ui/components/common/elementWrapper/BaseElementWrapper';
import ElementContextMenuTrigger from '../../ui/components/contextMenu/ElementContextMenu';
import {snapHandler} from '../../utils/element';
import useElementActions from '../../utils/hooks/slideUpdates/useElementActions';
import {getBorderSize} from '../../utils/style.utils';
import {usePresScaleContext} from '../../context/PresScaleContext';
import ElementTyped from './element.typed';
import {drag} from './utils/interactor.move';
import {resize} from './utils/interactor.resize';
import {rotate} from './utils/interactor.rotate';
import LineElementV2 from './geometry/LineElementV2';
import LineElementWrapper from '../../ui/components/common/elementWrapper/LineElementWrapper';
import ArrowElementV2 from './geometry/ArrowElementV2';

interface ElementComponentProps {
    element: IElement;
    cellWidth: number;
    cellHeight: number;
    onOptions?: (id: string, silent?: boolean) => void;
    assistPoints: Record<string, {x: number; y: number}[]>;
    index: number;
    hasErrors?: boolean;
}

function ElementComponent(props: ElementComponentProps): ReactElement {
    const {element, assistPoints, index, hasErrors, cellWidth, cellHeight, onOptions} = props;
    const {workspaceSize, scale} = usePresScaleContext();
    const clientWidth = workspaceSize.width;
    const clientHeight = workspaceSize.height;

    const {xPosition, yPosition, width, height, type, keyIndex, elementIndex, angle} = element;

    const {updateElement, updateElementFitContent} = useElementActions();

    const showGrid = useSelector(showGridSelector);
    const highlightedElementIndex = useSelector(highlightedElementIndexSelector);
    const dispatch = useDispatch();

    const [drawCords, setdrawCords] = useState<
        {
            x1: number | null;
            x2: number | null;
            y1: number | null;
            y2: number | null;
        }[]
    >([]);
    const rotationHandleName = useMemo(() => `rh-${keyIndex}`, [keyIndex]); // className
    const interactor = useMemo(() => interact(`#${keyIndex}`), [keyIndex]);
    const interactorRotation = useMemo(() => interact(`#${rotationHandleName}`), [rotationHandleName]);
    const isElementSelected = useMemo(() => highlightedElementIndex === index, [highlightedElementIndex, index]);
    const elementRef = useRef<HTMLDivElement>(null);

    const calculateElementPosition = useCallback(
        (x: number, y: number, snapFlag = true, event: Interact.DragEvent) => {
            // calculates position with snapping in mind
            const h = +element.height.slice(0, -1);
            const w = +element.width.slice(0, -1);
            const assist = snapHandler(
                assistPoints,
                x,
                y,
                w,
                h,
                clientWidth,
                clientHeight,
                showGrid ? cellWidth / 10 : 1.5,
                event,
                index,
                setdrawCords
            );
            let newX = x;
            let newY = y;

            if (showGrid && snapFlag) {
                const elementWidth = element.width.includes('%')
                    ? (Number(element.width.split('%')[0]) / 100) * clientWidth
                    : Number(element.width.split('px')[0]);
                if (newX < clientWidth - elementWidth - 1) {
                    newX -= newX % cellWidth;
                }
                const elementHeight = element.height.includes('%')
                    ? (Number(element.height.split('%')[0]) / 100) * clientHeight
                    : Number(element.height.split('px')[0]);
                if (newY < clientHeight - elementHeight - 1) {
                    newY -= newY % cellHeight;
                }
            }

            if (assist.x) {
                newX = assist.x;
            }
            if (assist.y) {
                newY = assist.y;
            }

            return {
                x: newX,
                y: newY,
            };
        },
        [assistPoints, cellHeight, cellWidth, clientHeight, clientWidth, element.height, element.width, index, showGrid]
    );

    const getElementPositionPx = useCallback((): {x: number; y: number} => {
        const getAsPx = (percentage: number, size: number): number => {
            if (!percentage) return 0;
            return (percentage / 100) * size;
        };

        return {
            x: getAsPx(xPosition, clientWidth),
            y: getAsPx(yPosition, clientHeight),
        };
    }, [clientWidth, clientHeight, xPosition, yPosition]);

    const coverSlide = isCoverHtmlElement(element);

    useEffect(() => {
        if (coverSlide) return;

        const getAspectRatio = (): number | undefined => {
            if (type === ELEMENT.IMAGE || type === ELEMENT.VIDEO) return element.widthHeightRatio || 1;
            if (type === ELEMENT.ELLIPSE && element.isCircle) return 1;
            return undefined;
        };

        const getResizeModifiers = (): any[] => {
            const baseModifiers = [
                interact.modifiers.restrictEdges({
                    outer: '#slide-workspace',
                }),
                interact.modifiers.restrictSize({
                    min: {width: 100, height: 40},
                }),
            ];
            const aspectRatio = getAspectRatio();
            if (aspectRatio) {
                return [
                    interact.modifiers.aspectRatio({
                        ratio: aspectRatio,
                        modifiers: baseModifiers,
                    }),
                ];
            }
            return baseModifiers;
        };

        interactor
            .resizable({
                allowFrom: `.c-baseElementWrapper`,
                ignoreFrom: `.${rotationHandleName}`,
                edges: {
                    top: `.c-baseElementWrapper__borderSquare--top, .c-baseElementWrapper__borderCircle--topLeft,
            .c-baseElementWrapper__borderCircle--topRight`,
                    bottom: `.c-baseElementWrapper__borderSquare--bottom, .c-baseElementWrapper__borderCircle--bottomLeft,
             .c-baseElementWrapper__borderCircle--bottomRight`,
                    left: `.c-baseElementWrapper__borderSquare--left, .c-baseElementWrapper__borderCircle--topLeft,
             .c-baseElementWrapper__borderCircle--bottomLeft`,
                    right: `.c-baseElementWrapper__borderSquare--right, .c-baseElementWrapper__borderCircle--topRight,
             .c-baseElementWrapper__borderCircle--bottomRight`,
                },
                listeners: {
                    start: (event) => {
                        // updateElementFitContent(elementIndex, element, false, true);
                        resize.start(event, element, getElementPositionPx());
                    },
                    move: (event) => {
                        resize.move(event, clientWidth, clientHeight, scale, getAspectRatio());
                    },
                    end: (event) => {
                        const targetEl = event.target as HTMLElement;
                        const elementPosition = calculateElementPosition(
                            Number(targetEl.dataset.x),
                            Number(targetEl.dataset.y),
                            false,
                            event
                        );

                        setdrawCords([]);
                        resize.end(event, elementPosition, clientWidth, clientHeight, (update) =>
                            updateElement(index, {...element, ...update, fitContent: false} as IElement)
                        );
                    },
                },
                modifiers: getResizeModifiers(),
            })
            .draggable({
                allowFrom: `#slide-element`,
                ignoreFrom: `
                    .${rotationHandleName}, 
                    .c-elementControl, 
                    .c-tiptap.is-active, 
                    .c-tiptap.is-focused, 
                    .c-tiptap.is-fullscreen, 
                    .c-imageMarkup, 
                    .c-videoElement video
                    `,
                listeners: {
                    start: (event) => {
                        drag.start(event, getElementPositionPx());
                    },
                    move: (event) => {
                        drag.move(event, scale);
                    },
                    end: (event) => {
                        setdrawCords([]);
                        const targetEl = event.target as HTMLElement;
                        const elementPosition = calculateElementPosition(
                            Number(targetEl.dataset.x),
                            Number(targetEl.dataset.y),
                            false,
                            event
                        );
                        drag.end(event, elementPosition, clientWidth, clientHeight, (update) =>
                            updateElement(index, {...element, ...update} as IElement)
                        );
                    },
                },
                modifiers: [
                    interact.modifiers.restrictRect({
                        restriction: '#slide-workspace',
                    }),
                ],
            });
    }, [
        calculateElementPosition,
        clientHeight,
        clientWidth,
        scale,
        dispatch,
        element,
        elementIndex,
        getElementPositionPx,
        index,
        interactor,
        rotationHandleName,
        type,
        updateElement,
        updateElementFitContent,
        coverSlide,
    ]);

    useEffect(() => {
        if (coverSlide) return;

        interactorRotation.draggable({
            listeners: {
                start: (event) => {
                    rotate.start(event, elementRef.current as HTMLElement, element);
                },
                move: (event) => {
                    rotate.move(event, elementRef.current as HTMLElement);
                    updateElementFitContent(elementIndex, element, false, true);
                },
                end: (event) => {
                    setdrawCords([]);
                    rotate.end(event, element, elementRef.current as HTMLElement, (update) =>
                        updateElement(index, {...element, ...update} as IElement)
                    );
                },
            },
        });
    }, [
        dispatch,
        element,
        elementIndex,
        index,
        interactorRotation,
        rotationHandleName,
        updateElement,
        updateElementFitContent,
        coverSlide,
    ]);

    function updateElementSize(): void {
        if (!observedDiv.current) return;
        const borderSize = getBorderSize(observedDiv.current.parentElement?.parentElement);
        const contentHeight = observedDiv.current.offsetHeight;
        const height = Math.min(((contentHeight + borderSize.vertical) / clientHeight) * 100, 100);

        if (parseFloat(element.height) === height) return;

        const heightAndYPosition = height + element.yPosition;
        if (heightAndYPosition >= 100) {
            const yPosition = 100 - height;
            updateElement(elementIndex, {...element, height: `${height}%`, yPosition});
        } else {
            updateElement(elementIndex, {...element, height: `${height}%`});
        }
    }

    const debouncedUpdateElementSize = useDebouncedCallback(updateElementSize, 50);

    const observedDiv = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!observedDiv.current || !('fitContent' in element && element.fitContent)) {
            return () => {
                /* empty */
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            debouncedUpdateElementSize();
        });

        resizeObserver.observe(observedDiv.current);

        return function cleanup() {
            resizeObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [observedDiv.current, (element as IElementQuestion).fitContent]);

    if (element.type === ELEMENT.MODAL) return <></>;

    return (
        <>
            {drawCords[0] ? (
                <svg
                    className="c-presGrid"
                    style={{overflow: 'hidden'}}
                    width={clientWidth}
                    height={clientHeight}
                    viewBox={`0 0 ${clientWidth} ${clientHeight}`}
                >
                    {drawCords.map((line, i) => (
                        <line
                            key={i}
                            x1={`${line.x1}`}
                            x2={`${line.x2}`}
                            y1={`${line.y1}`}
                            y2={`${line.y2}`}
                            stroke="orange"
                            strokeWidth="1.2"
                        />
                    ))}
                </svg>
            ) : null}

            <ElementContextMenuTrigger element={element} elementIndex={elementIndex}>
                {element.type === ELEMENT.LINE_V2 || element.type === ELEMENT.ARROW_V2 ? (
                    <LineElementWrapper element={element} elementIndex={elementIndex} onOptions={onOptions} isActive={isElementSelected}>
                        {element.type === ELEMENT.LINE_V2 && <LineElementV2 element={element} />}
                        {element.type === ELEMENT.ARROW_V2 && <ArrowElementV2 element={element} />}
                    </LineElementWrapper>
                ) : (
                    <div
                        id={`${keyIndex}`}
                        ref={elementRef}
                        className="u-absolute slide-element"
                        style={{
                            top: `${yPosition}%`,
                            left: `${xPosition}%`,
                            width,
                            height,
                            transform: `rotate(${angle}rad)`,
                            ...getElementShadow(element.boxShadow, element.questionBackgroundBorderRadius, element.questionBackgroundColor),
                        }}
                    >
                        <BaseElementWrapper
                            isActive={isElementSelected}
                            isError={hasErrors}
                            onOptions={onOptions}
                            element={element}
                            lockGeometry={coverSlide}
                        >
                            <div ref={observedDiv} style={{height: element.fitContent ? 'fit-content' : '100%'}}>
                                <ElementTyped element={element}>
                                    {!coverSlide && (
                                        <div id={rotationHandleName} className="u-absolute rotate-icon">
                                            <div className={rotationHandleName}>
                                                <Icon icon="fa-solid:sync" className="u-events-none" />
                                            </div>
                                        </div>
                                    )}
                                </ElementTyped>
                            </div>
                        </BaseElementWrapper>
                    </div>
                )}
            </ElementContextMenuTrigger>
        </>
    );
}

export default ElementComponent;
